import React, { useEffect, useId, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useModal from "components/modal/useModal";
import UseContext from "hooks/useContext";
import Button from "components/button";
import IconTooltip from "components/iconTooltip/IconTooltip";
import MissionTemplateSummary from "pages/missionTemplates/components/MissionTemplateSummary";
import { getTemplateTitle } from "pages/missionTemplates/utils/templateListing";
import { CloseModal, CopyIcon, Pencil, TrashIcon } from "assets/icons";

const PreviewMissionTemplate = () => {
    const navigate = useNavigate();
    const { toggle } = useModal();
    const {
        previewMissionTemplate,
        setPreviewMissionTemplate,
        setSelectedMissionTemplate,
        setPendingDeleteMissionTemplate,
        setPendingDuplicateMissionTemplate,
        setModalType,
        setPage,
        language,
    } = UseContext();
    const titleId = useId();
    const panelRef = useRef(null);

    const t = previewMissionTemplate;
    const hasTemplate = !!t?._id;
    const isBelowMinimumWage = !!t?.isBelowMinimumWage;
    const canUseTemplate = hasTemplate && !isBelowMinimumWage;

    // The panel remounts after a cancelled delete, so the card to refocus is
    // found by id rather than captured on mount.
    const close = () => {
        const templateId = t?._id;
        setPreviewMissionTemplate(null);
        toggle();
        if (templateId) {
            document
                .querySelector(`[data-template-id="${templateId}"]`)
                ?.focus();
        }
    };

    // The listener is registered once, so it reads the latest close through a ref.
    const closeRef = useRef(close);
    closeRef.current = close;

    // Tab is trapped by hand instead of making #root inert: close() refocuses
    // the card synchronously, which would fail while #root is still inert.
    useEffect(() => {
        panelRef.current?.focus();
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                closeRef.current();
                return;
            }
            const panel = panelRef.current;
            if (event.key !== "Tab" || !panel) return;
            const focusable = panel.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;
            const isOutside = active === panel || !panel.contains(active);
            if (event.shiftKey && (isOutside || active === first)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (isOutside || active === last)) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    const handleModifier = () => {
        if (!hasTemplate) {
            close();
            return;
        }
        navigate(`/missionTemplates/edit/${t._id}`);
        setPreviewMissionTemplate(null);
        toggle();
    };

    const handleDelete = () => {
        if (!hasTemplate) return;
        setPendingDeleteMissionTemplate(t);
        // Swaps the panel for the confirmation without closing the modal layer:
        // the preview stays in context so cancelling can bring it back.
        setModalType("missionTemplate_delete");
    };

    const handleDuplicate = () => {
        if (!hasTemplate) return;
        setPendingDuplicateMissionTemplate(t);
        setModalType("missionTemplate_duplicate");
    };

    const handleCreateMission = () => {
        if (!hasTemplate) {
            close();
            return;
        }
        if (isBelowMinimumWage) {
            toast.error(
                language.getValue(
                    "missionTemplate.preview.belowMinimumWageToast",
                    "Cette fiche est inférieure au SMIC, mettez-la à jour avant utilisation."
                )
            );
            return;
        }
        setSelectedMissionTemplate(t);
        setPage(3);
        navigate("/createOfMission");
        setPreviewMissionTemplate(null);
        toggle();
    };

    const deleteLabel = language.getValue(
        "missionTemplate.preview.delete",
        "Supprimer"
    );
    const duplicateLabel = language.getValue(
        "missionTemplate.preview.duplicate",
        "Dupliquer"
    );

    const title =
        getTemplateTitle(t) ||
        language.getValue("missionTemplate.preview.defaultTitle", "Fiche de poste");

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className="flex h-full min-h-0 flex-col focus:outline-none"
        >
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
                <h2
                    id={titleId}
                    className="min-w-0 break-words ft-xl font-semibold text-gray-900"
                >
                    {title}
                </h2>
                <IconTooltip
                    label={language.getValue(
                        "missionTemplate.preview.closeTooltip",
                        "Fermer"
                    )}
                    placement="bottom"
                >
                    <button
                        type="button"
                        onClick={close}
                        aria-label={language.getValue(
                            "missionTemplate.preview.closeLabel",
                            "Fermer l'aperçu"
                        )}
                        className="shrink-0 rounded-md p-2 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
                    >
                        <CloseModal />
                    </button>
                </IconTooltip>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
                <MissionTemplateSummary apiTemplate={t} />
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-100 px-6 py-4">
                {isBelowMinimumWage && (
                    <p className="text-right ft-xs text-red-700">
                        {language.getValue(
                            "missionTemplate.preview.belowMinimumWage",
                            "Taux horaire inférieur au SMIC : modifiez la fiche pour pouvoir l'utiliser."
                        )}
                    </p>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <IconTooltip label={deleteLabel}>
                            <Button
                                size={"LG"}
                                color={"SECONDARY"}
                                className="!border-red-200 hover:!bg-red-50"
                                onClick={handleDelete}
                                disabled={!hasTemplate}
                                ariaLabel={deleteLabel}
                            >
                                <TrashIcon wh={18} color="#B91C1C" />
                            </Button>
                        </IconTooltip>
                        <IconTooltip label={duplicateLabel}>
                            <Button
                                size={"LG"}
                                color={"SECONDARY"}
                                onClick={handleDuplicate}
                                disabled={!hasTemplate}
                                ariaLabel={duplicateLabel}
                            >
                                <CopyIcon wh={18} color="#374151" />
                            </Button>
                        </IconTooltip>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            size={"LG"}
                            color={"SECONDARY"}
                            onClick={handleModifier}
                            disabled={!hasTemplate}
                        >
                            <span aria-hidden="true">
                                <Pencil wh={18} color="#4B5563" />
                            </span>
                            {language.getValue(
                                "missionTemplate.preview.edit",
                                "Modifier"
                            )}
                        </Button>
                        <Button
                            size={"LG"}
                            color={canUseTemplate ? "PRIMARY" : "DISABLED"}
                            className={canUseTemplate ? "" : "cursor-not-allowed"}
                            onClick={handleCreateMission}
                            disabled={!canUseTemplate}
                        >
                            {language.getValue(
                                "missionTemplate.preview.createMission",
                                "Créer une mission"
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreviewMissionTemplate;
